import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import NavBar from "../../components/common/NavBar";
import { useProductScope, scopeTitle, scopeName, scopeFilePrefix } from "../../contexts/ProductScope";
import { openDataAvailable } from "../../config/openData";
import PopulationBaseline from "../../components/emergency/PopulationBaseline";
import BaselineCellLayer from "../../components/emergency/BaselineCellLayer";
import BaselineLayerControl from "../../components/emergency/BaselineLayerControl";
import { useBaselineLayer } from "../../hooks/emergency/useBaselineLayer";
import AreaTimelineChart, { TimelineSeries } from "../../components/realtime/AreaTimelineChart";
import Spinner from "../../components/common/Spinner";
import RealtimeAreaMap, { AreaPolygon } from "../../components/common/RealtimeAreaMap";
import { TrendInline, trafficTone } from "../../components/realtime/TrendBits";
import { readZones, liveTotal, trafficOf, ZoneReading } from "../../realtime/zoneReadings";
import { MapTotals, MapStamp, MapModeSwitch, MapMode, TotalRow } from "../../components/realtime/MapOverlays";
import { AreaDetailPanel } from "../../components/realtime/ZoneDetails";
import { useDashboardConfig, GroupEditing } from "../../hooks/realtime/useDashboardConfig";
import { useReadOnly } from "../../hooks/useReadOnly";
import { useIsPhone } from "../../hooks/useIsPhone";
import {
  realtimeApi,
  RealtimeRow,
  RealtimeBucket,
} from "../../services/realtimeApi";
import { usePollingRefresh } from "../../hooks/realtime/usePollingRefresh";
import { useNightFloor, localNightFloorStore } from "../../hooks/realtime/useNightFloor";
import { useRealtimeCountry } from "../../hooks/realtime/useRealtimeCountry";
import { useRoamingPulse } from "../../hooks/realtime/useRoamingPulse";
import { userMapsApi } from "../../services/userMapsApi";
import { projectsApi, Project } from "../../services/projectsApi";
import { realtimeDataIsHere, realtimeEnvLabel } from "../../utils/realtimeEnv";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import SharedProjectsWarning from "../../components/monitoring/SharedProjectsWarning";
import {
  monitorableMembers,
  sharedMembers,
  bulkSetMonitoring,
  SharedMember,
} from "../../services/projectMonitoring";
import { DateRange, DEFAULT_RANGE } from "../../components/common/DateRangePicker";
import NightFloorControl from "../../components/realtime/NightFloorControl";
import CountryView from "../../components/realtime/CountryView";
import RoamingPulse from "../../components/realtime/RoamingPulse";
import { RealtimeTabs, RealtimeViewControls, RealtimeView, isRealtimeView } from "../../components/realtime/RealtimeViewSwitch";
import { DayReport } from "./ReportPage";
import { dailyCountryRows, countrySplitFromDaily, rangeLabelFor } from "../../utils/realtimeCountry";
import { downloadCsv, csvFilename } from "../../utils/csv";
import { theme } from "../../config/theme";
import { useThemeColors } from "../../hooks/useThemeColors";


interface Member {
  id: string;
  name: string;
  color: string;
  wkt?: string;
  bqProjectId?: string;
  // y/max are baseline-adjusted when the night floor is on; yRaw/maxRaw never are.
  // The chart plots the adjusted series; the map always shows raw counts.
  series: ZoneReading["points"];
  reading: ZoneReading | null; // null until the area has data
  baselineAdjusted: boolean; // night floor deducted from this member's series
}

const ProjectDashboard: React.FC = () => {
  const productScope = useProductScope();
  const isEmergency = productScope.product === "emergency";
  // The baseline tab and layer need the open data API (or explicit draft mocks); without it they are hidden.
  const baselineAvailable = isEmergency && openDataAvailable();
  // Series identity colours for the multi-area chart (slot 1 is the brand green).
  const colors = useThemeColors();
  const [searchParams, setSearchParams] = useSearchParams();
  // Two ways in: an explicit ?ids=&name= (ad-hoc selection) or ?project=<id>
  // (a saved project, resolved from projectsApi).
  const projectId = searchParams.get("project");
  const urlIds = useMemo(
    () => (searchParams.get("ids") || "").split(",").map((s) => s.trim()).filter(Boolean),
    [searchParams]
  );
  const urlName = searchParams.get("name") || "Selection";

  const [project, setProject] = useState<Project | null>(null);
  useEffect(() => {
    if (!projectId) {
      setProject(null);
      return;
    }
    // Remember this as the last dashboard opened (for the home "resume").
    projectsApi.recordOpened(projectId);
    let cancelled = false;
    projectsApi
      .get(projectId)
      .then((p) => !cancelled && setProject(p))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const ids = useMemo(
    () => (projectId ? project?.areaIds ?? [] : urlIds),
    [projectId, project, urlIds]
  );
  const name = projectId ? project?.name ?? "Project" : urlName;

  const [rows, setRows] = useState<RealtimeRow[]>([]);
  // Granularity the backend chose for the current range (it aggregates wide
  // ranges rather than truncating them).
  const [bucket, setBucket] = useState<RealtimeBucket>("raw");
  const bucketed = bucket !== "raw";
  const [areas, setAreas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [monitoredIds, setMonitoredIds] = useState<Set<string>>(new Set());
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  // Night-floor baseline: one shared toggle + night range for the project,
  // with floors computed server-side PER MEMBER (each area's own ambient
  // background). Fetch, persistence and the missing-endpoint case live in
  // the hook.
  const nightFloorStore = useMemo(
    () => localNightFloorStore(`telcofy.nightfloor.project.${projectId ?? "selection"}`),
    [projectId]
  );
  const nightFloor = useNightFloor({ store: nightFloorStore, zoneIds: ids });
  // The open tab lives in the address (?tab=), so a refresh, the back button
  // and a shared link all land on it. Tabs this dashboard can't show fall back to the timeline.
  const tabParam = searchParams.get("tab");
  const view: RealtimeView =
    isRealtimeView(tabParam) && (tabParam !== "baseline" || baselineAvailable) && (tabParam !== "report" || !!projectId)
      ? tabParam
      : "timeline";
  const setView = useCallback(
    (v: RealtimeView) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === "timeline") next.delete("tab");
          else next.set("tab", v);
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );
  // The overall map follows the tab (roaming → international) until the user overrides.
  const [mapMode, setMapMode] = useState<MapMode>("present");
  useEffect(() => setMapMode(view === "roaming" ? "international" : "present"), [view]);
  const [mapOpen, setMapOpen] = useState(true);
  // On a phone the map keeps only its totals; the stamp and switches move off it.
  const phone = useIsPhone();
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [data, geo, monitored, projects] = await Promise.all([
          realtimeApi.getRealtimeData({
            datetimeFrom: dateRange.from ?? undefined,
            datetimeTo: dateRange.to ?? undefined,
          }),
          userMapsApi.loadGeometries(),
          realtimeApi.getMonitoredMaps().catch(() => ({ monitored_map_ids: [] as string[] })),
          projectsApi.list().catch(() => [] as Project[]),
        ]);
        if (cancelled) return;
        setRows(data.rows);
        setBucket(data.bucket);
        setAreas(geo.maps);
        setMonitoredIds(new Set(monitored.monitored_map_ids || []));
        setAllProjects(projects);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : `Failed to load ${scopeName(productScope, "realtime")} data`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateRange, productScope]);

  // Auto-refresh while the range is open-ended: silently re-fetch the realtime
  // rows (backend samples ~every 10 min). No spinner; paused when the range has a
  // fixed end or the tab is hidden.
  const isLive = dateRange.to === null;
  usePollingRefresh(
    () => {
      realtimeApi
        .getRealtimeData({
          datetimeFrom: dateRange.from ?? undefined,
          datetimeTo: dateRange.to ?? undefined,
        })
        .then((d) => {
          setRows(d.rows);
          setBucket(d.bucket);
        })
        .catch(() => {});
    },
    60000,
    isLive
  );

  const idSet = useMemo(() => new Set(ids), [ids]);
  // Per-member night floors over the selected nights. A member with too little
  // night data has no entry and stays raw — only a member's OWN floor is ever
  // deducted from it.
  const floorByMember = nightFloor.floorByZone;
  const nights = nightFloor.nights;

  // Each area's reading (only where data has accumulated), night-floor-adjusted when on.
  const readings = useMemo(
    () => readZones(rows, ids, { nightFloors: nightFloor.nightFloors, bucketed }),
    [rows, ids, nightFloor.nightFloors, bucketed]
  );

  // One entry per selected area, with its name/geometry (always) and its reading.
  const members: Member[] = useMemo(() => {
    return ids.map((id, i) => {
      const area = areas.find((a) => a.id === id);
      const reading = readings.get(id) ?? null;
      return {
        id,
        name: area?.name || id,
        color: colors.series[i % colors.series.length],
        wkt: area?.geometry,
        bqProjectId: area?.bqProjectId,
        series: reading?.points ?? [],
        reading,
        baselineAdjusted: reading?.adjusted ?? false,
      };
    });
  }, [ids, areas, readings, colors]);
  const memberWkts = useMemo(() => members.filter((m) => m.wkt).map((m) => m.wkt as string), [members]);
  // Emergency only: the optional 250 m cell layer on the map. Stays off (and idle) unless switched on.
  const baselineLayer = useBaselineLayer(baselineAvailable ? memberWkts : []);

  // Shared country + roaming data for this project's members. Both hooks are
  // lazy (nothing is fetched until their tab is open), and the aggregation is
  // the same pure code the single-area dashboard runs — only the scope differs.
  const country = useRealtimeCountry({ enabled: view === "country", dateRange });
  const { days: countryDays, rows: countryRowsDaily } = useMemo(
    () => dailyCountryRows(country.rows, idSet),
    [country.rows, idSet]
  );
  const countrySplit = useMemo(() => countrySplitFromDaily(countryRowsDaily), [countryRowsDaily]);
  const rangeLabel = rangeLabelFor(dateRange);
  // Always on: the overall map shows international figures whichever tab is open.
  const roaming = useRoamingPulse({ enabled: true, scope: idSet });
  const zonesById = useMemo(() => new Map(roaming.zones.map((z) => [z.id, z])), [roaming.zones]);
  const wktById = useMemo(
    () => new Map(members.filter((m) => m.wkt).map((m) => [m.id, m.wkt as string])),
    [members]
  );

  // One line per area, in the area's identity colour.
  const chartSeries = useMemo<TimelineSeries[]>(
    () =>
      members
        .filter((m) => m.series.length)
        .map((m) => ({ label: m.name, color: m.color, data: m.series.map((p) => ({ x: p.x, y: p.y })) })),
    [members]
  );

  // Per-area stats for the leaderboard, ranked by current count.
  const leaderboard = useMemo(() => {
    return members
      .map((m) => {
        const r = m.reading;
        if (!r)
          return { ...m, now: null as number | null, nowRaw: null as number | null, peak: null, peakRaw: null, delta: null, pct: null };
        return {
          ...m,
          now: r.now,
          nowRaw: r.nowRaw,
          peak: r.peak,
          peakRaw: r.peakRaw,
          delta: r.delta, // a difference, so the night floor cancels out
          pct: r.pct,
        };
      })
      .sort((a, b) => (b.now ?? -1) - (a.now ?? -1));
  }, [members]);

  const withData = leaderboard.filter((m) => m.now != null);
  const combined = withData.reduce((s, m) => s + (m.now || 0), 0);
  const busiest = withData[0];
  // The coverage map carries each area's live figure and 1h direction as a
  // label, so the map reads on its own; clicking an area opens its detail panel.
  const mapPolygons = useMemo<AreaPolygon[]>(
    () =>
      leaderboard
        .filter((m) => m.wkt)
        .map((m) => ({
          id: m.id,
          name: m.name,
          wkt: m.wkt as string,
          // Traffic light by 1h change, not the chart palette — the map should read at a glance.
        color: trafficTone[m.reading?.traffic ?? "none"],
        label:
          m.nowRaw != null ? (
            <span className="flex items-center gap-1.5">
              <span className="font-medium text-ink">{m.nowRaw.toLocaleString()}</span>
                {m.delta != null && <TrendInline v={m.delta} />}
              </span>
            ) : (
              <span className="text-ink-faint">no data</span>
            ),
        })),
    [leaderboard]
  );
  const selectedArea = leaderboard.find((m) => m.id === selectedAreaId) ?? null;

  // International view of the same polygons: tinted by direction, labelled with the
  // international count and its change over the roaming interval.
  const intlPolygons = useMemo<AreaPolygon[]>(
    () =>
      leaderboard
        .filter((m) => m.wkt)
        .map((m) => {
          const z = zonesById.get(m.id);
          return {
            id: m.id,
            name: m.name,
            wkt: m.wkt as string,
            color: z ? trafficTone[trafficOf(z.delta, z.intlNow - z.delta)] : theme.flat,
            label: z ? (
              <span className="flex items-center gap-1.5">
                <span className="font-medium text-ink">{z.intlNow.toLocaleString()}</span>
                <TrendInline v={z.delta} />
              </span>
            ) : (
              <span className="text-ink-faint">no roaming data</span>
            ),
          };
        }),
    [leaderboard, zonesById]
  );
  const showIntl = mapMode === "international" && roaming.zones.length > 0;

  // Named groups of areas with their own running total (e.g. "Sentrum" inside
// "Oslo"). Dashboard config — device-local for now, see services/dashboardConfig.
const config = useDashboardConfig(projectId);
const groups = config.groups;
const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
const editingGroup = groups.find((g) => g.id === editingGroupId) ?? null;
// Read-only (support view or a viewer): totals show but can't be edited.
const { readOnly } = useReadOnly();
const groupEditing = useMemo<GroupEditing | undefined>(
  () =>
    config.enabled && !readOnly
      ? {
          group: editingGroup,
          storage: config.storage,
          error: config.error,
          onEdit: setEditingGroupId,
          onAdd: () => setEditingGroupId(config.addGroup().id),
          onRename: (n) => editingGroup && config.renameGroup(editingGroup.id, n),
          onDelete: () => {
            if (editingGroup) config.removeGroup(editingGroup.id);
            setEditingGroupId(null);
          },
          onSetShow: (show) => editingGroup && config.setGroupShow(editingGroup.id, show),
          onDone: () => setEditingGroupId(null),
          onToggleArea: (areaId) => editingGroup && config.toggleArea(editingGroup.id, areaId),
        }
      : undefined,
  [config, editingGroup, readOnly]
);
const mapTotals = useMemo<TotalRow[]>(() => {
  // Both figures on every row: present (from the timeline data) and international
  // (from the country data) — the point of one map is not having to switch tabs.
  // Present stays raw on purpose: the international figure beside it can't be
  // baseline-adjusted (see liveTotal).
  const row = (label: string, id: string | undefined, ids: Set<string> | null, show?: TotalRow["show"]) => {
    const t = liveTotal(readings, roaming.zones, ids);
    return {
      id,
      show,
      label,
      present: t.present,
      ...(t.intl != null ? { intl: t.intl, delta: t.intlDelta } : {}),
    };
  };
  return [
    row(`Total ${name}`, undefined, null),
    ...groups.map((g) => row(`Total ${g.name || "…"}`, g.id, new Set(g.areaIds), g.show)),
  ];
}, [readings, roaming.zones, groups, name]);
const coverageUpdatedAt = useMemo(
    () => members.reduce<number | null>((t, m) => (m.series.length ? Math.max(t ?? 0, m.series[m.series.length - 1].x) : t), null),
    [members]
  );
  const otherEnvCount = members.filter((m) => !realtimeDataIsHere(m.bqProjectId)).length;

  // Bulk "live monitoring" control for the project's monitorable (custom-polygon)
  // members. Monitoring is per-polygon, so disabling one that's shared with other
  // projects stops it there too — hence the confirm on disable.
  const monitorMembers = useMemo(
    () => monitorableMembers(ids, areas, monitoredIds),
    [ids, areas, monitoredIds]
  );
  const monitoredCount = monitorMembers.filter((m) => m.monitored).length;
  const [monitorBusy, setMonitorBusy] = useState(false);
  const [confirmShared, setConfirmShared] = useState<SharedMember[] | null>(null);

  const refreshMonitored = useCallback(async () => {
    try {
      const { monitored_map_ids } = await realtimeApi.getMonitoredMaps();
      setMonitoredIds(new Set(monitored_map_ids || []));
    } catch {
      /* keep last state */
    }
  }, []);

  const enableAll = async () => {
    const toEnable = monitorMembers.filter((m) => !m.monitored).map((m) => m.id);
    if (!toEnable.length) return;
    setMonitorBusy(true);
    await bulkSetMonitoring(toEnable, true);
    await refreshMonitored();
    setMonitorBusy(false);
  };

  const requestDisableAll = () => {
    if (!monitoredCount) return;
    const active = monitorMembers.filter((m) => m.monitored);
    setConfirmShared(sharedMembers(active, allProjects, projectId ?? undefined));
  };

  const confirmDisableAll = async () => {
    const toDisable = monitorMembers.filter((m) => m.monitored).map((m) => m.id);
    setMonitorBusy(true);
    await bulkSetMonitoring(toDisable, false);
    await refreshMonitored();
    setMonitorBusy(false);
    setConfirmShared(null);
  };

  // Only actually rendered by NightFloorControl once it has nights to show
  // (it falls back to its own "Measuring…" / "No night-time data" copy
  // otherwise), so no separate "not loaded yet" branch is needed here.
  const nightFloorStatus =
    floorByMember.size === 0
      ? "Not enough night-time samples in the selected nights for any area — showing raw counts."
      : `${nightFloor.enabled ? "Deducting" : "Would deduct"} each area's own night floor (median of 02–05h) — ${floorByMember.size} of ${ids.length} area${ids.length === 1 ? "" : "s"} ${nightFloor.enabled ? "adjusted" : "have enough night data"}${nightFloor.enabled && floorByMember.size < ids.length ? "; the rest stay raw" : ""}. The chart and the ranked table use it; the map always shows raw counts.`;

  // Download the plotted per-area series (baseline-adjusted when on) in long
  // form — one row per plotted point (timestamp, area, subscribers). Areas
  // sample at staggered seconds, so a wide timestamp×area matrix would be
  // almost all blanks; long form stays tidy and imports cleanly everywhere.
  const exportCsv = () => {
    const rows: (string | number)[][] = [["timestamp", "area", "subscribers"]];
    members
      .filter((m) => m.series.length)
      .flatMap((m) => m.series.map((p) => ({ t: p.x, area: m.name, y: p.y })))
      .sort((a, b) => a.t - b.t || a.area.localeCompare(b.area))
      .forEach((r) => rows.push([new Date(r.t).toISOString(), r.area, r.y]));
    downloadCsv(csvFilename(scopeFilePrefix(productScope, "realtime"), name), rows);
  };

  const modeSwitch = (
    <MapModeSwitch
      mode={mapMode}
      onChange={setMapMode}
      internationalAvailable={!roaming.notEnabled}
      loading={roaming.loading && roaming.zones.length === 0}
    />
  );

  return (
    <div className="min-h-screen bg-paper-200 print:bg-transparent">
      <NavBar title={scopeTitle(productScope, "Realtime")} showBackButton={true} />

      {/* Printing is the day report's job: everything around it stays off the page. */}
      <div className="mx-auto max-w-none px-4 py-10 sm:px-6 lg:px-8 2xl:px-10 print:p-0">
        <div className="mb-8 print:hidden">
          <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-ink-faint">
            {scopeTitle(productScope, `Realtime · ${projectId ? "project" : "selection"}`, projectId ? "project" : "selection")}
            {isLive && withData.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-ok-soft px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-ok-ink">
                <span className="h-1.5 w-1.5 rounded-full bg-ok animate-pulse" />
                Live
              </span>
            )}
            {isLive && withData.length === 0 && monitoredCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-warn-soft px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-warn-ink">
                <span className="h-1.5 w-1.5 rounded-full bg-warn" />
                Enabled · no data
              </span>
            )}
          </p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-ink">{name}</h1>
          <p className="text-sm text-ink-muted">
            {members.length} areas · {withData.length} with live data
            {otherEnvCount > 0 && ` · ${otherEnvCount} in another environment`}
          </p>
        </div>

        {/* Tabs first: they decide what the rest of the page is. On a phone the
            strip stays in reach under the nav bar while the page scrolls. */}
        <RealtimeTabs
          view={view}
          onView={setView}
          showBaseline={baselineAvailable}
          showReport={!!projectId}
          className="sticky top-16 z-20 mb-6 bg-paper-200 py-2 print:hidden md:static md:bg-transparent md:py-0"
        />

        {/* The map leads every live tab: both figures per total, editable groups.
            The day report brings its own map of its own day, so it hides this one. */}
        {view !== "report" && members.some((m) => m.wkt) && (
          <div className="mb-6 overflow-hidden rounded-2xl border border-paper-300/70 bg-surface shadow-soft">
            <div className="flex items-center justify-between border-b border-paper-200 px-4 py-3">
              <h2 className="font-display text-lg font-semibold text-ink">Map</h2>
              <div className="flex items-center gap-3">
                {/* On a phone the corner overlays leave the map: the stamp sits here, the switches below. */}
                {phone && <MapStamp at={showIntl ? roaming.updatedAt : coverageUpdatedAt} />}
                <span className="hidden text-xs text-ink-faint md:inline">click a zone for details{readOnly ? "" : " · pencil on a total to edit its zones"}</span>
                <button
                  onClick={() => setMapOpen((o) => !o)}
                  className="rounded-lg border border-paper-300 px-2.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-paper-100 hover:text-ink"
                >
                  {mapOpen ? "Hide map" : "Show map"}
                </button>
              </div>
            </div>
            {mapOpen && phone && (
              <div className="flex flex-wrap items-center gap-2 border-b border-paper-200 px-3 py-2">
                {baselineAvailable && <BaselineLayerControl layer={baselineLayer} />}
                {modeSwitch}
              </div>
            )}
            {mapOpen && (
              <div className="h-[60svh] min-h-[20rem] md:h-80 lg:h-[32rem] 2xl:h-[42rem]">
                <RealtimeAreaMap
                  // Same fill in both views: the 0.15 dates from when Present used the
                  // multi-colour chart palette and had to stay faint. Both views are the
                  // traffic light now, so they read the same.
                  polygons={showIntl ? intlPolygons : mapPolygons}
                  fillOpacity={0.28}
                  selectedId={selectedAreaId}
                  onSelect={(id) => (editingGroup && groupEditing ? id && groupEditing.onToggleArea(id) : setSelectedAreaId(id))}
                  highlightIds={editingGroup ? new Set(editingGroup.areaIds) : null}
                  overlayTopLeft={<MapTotals rows={mapTotals} editing={groupEditing} />}
                  overlayTopRight={<MapStamp at={showIntl ? roaming.updatedAt : coverageUpdatedAt} />}
                  overlayBottomLeft={
                    baselineAvailable ? (
                      <div className="flex flex-col items-start gap-2">
                        <BaselineLayerControl layer={baselineLayer} />
                        {modeSwitch}
                      </div>
                    ) : (
                      modeSwitch
                    )
                  }
                  underlay={baselineAvailable && baselineLayer.on && baselineLayer.data ? <BaselineCellLayer cells={baselineLayer.data.cells} /> : undefined}
                  panel={
                    selectedArea && !editingGroup && (
                      <AreaDetailPanel
                        name={selectedArea.name}
                        presence={{
                          now: selectedArea.nowRaw,
                          peak: selectedArea.peakRaw,
                          delta: selectedArea.delta,
                          series: selectedArea.series.map((pt) => pt.yRaw),
                        }}
                        zone={zonesById.get(selectedArea.id) ?? null}
                        intervalLabel={`${roaming.interval} min`}
                        onClose={() => setSelectedAreaId(null)}
                      />
                    )
                  }
                />
              </div>
            )}
          </div>
        )}

        {view !== "baseline" && view !== "report" && (
          <div className="mb-6 flex justify-end">
            <RealtimeViewControls
              view={view}
              onDateRange={setDateRange}
              rangeLabel={rangeLabel}
              roamingInterval={roaming.interval}
              onRoamingInterval={roaming.setInterval}
            />
          </div>
        )}

        {view === "timeline" && !loading && !error && monitorMembers.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-paper-300/70 bg-surface px-5 py-4 shadow-soft">
            <div>
              <p className="text-sm font-semibold text-ink">Live monitoring</p>
              <p className="text-xs text-ink-muted">
                {monitoredCount} of {monitorMembers.length} area
                {monitorMembers.length === 1 ? "" : "s"} monitored
              </p>
            </div>
            {!readOnly && <div className="flex gap-2">
              <button
                onClick={enableAll}
                disabled={monitorBusy || monitoredCount === monitorMembers.length}
                className="rounded-lg border border-paper-300 px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-paper-100 disabled:opacity-40"
              >
                Enable all
              </button>
              <button
                onClick={requestDisableAll}
                disabled={monitorBusy || monitoredCount === 0}
                className="rounded-lg border border-paper-300 px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-paper-100 disabled:opacity-40"
              >
                Disable all
              </button>
            </div>}
          </div>
        )}

        {view === "timeline" && !loading && !error && ids.length > 0 && !nightFloor.unavailable && (
          <NightFloorControl
            loading={nightFloor.loading}
            nights={nights}
            enabled={nightFloor.enabled}
            onToggle={nightFloor.setEnabled}
            fromNight={nightFloor.range?.from ?? null}
            toNight={nightFloor.range?.to ?? null}
            onRange={nightFloor.setRange}
            status={nightFloorStatus}
          />
        )}

        {view === "report" ? (
          <DayReport />
        ) : view === "baseline" && baselineAvailable ? (
          <PopulationBaseline geometries={memberWkts} areaLabel={name} />
        ) : view === "roaming" ? (
          <RoamingPulse
            loading={roaming.loading}
            notEnabled={roaming.notEnabled}
            error={roaming.error}
            zones={roaming.zones}
            updatedAt={roaming.updatedAt}
            intervalLabel={`${roaming.interval} min`}
            wktById={wktById}
            title={name}
            groups={groups}
            groupEditing={groupEditing}
            showMap={false}
          />
        ) : view === "country" ? (
          <CountryView
            loading={country.loading}
            notEnabled={country.notEnabled}
            error={country.error}
            days={countryDays}
            rows={countryRowsDaily}
            split={countrySplit}
            rangeLabel={rangeLabel}
          />
        ) : loading && !rows.length ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-bad-line bg-bad-soft p-6 text-bad-ink">{error}</div>
        ) : !members.length ? (
          <div className="rounded-2xl border border-paper-300/70 bg-surface p-10 text-center text-ink-muted">
            No areas selected.
          </div>
        ) : (
          <>
            {/* KPI tiles */}
            <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
                <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">Areas</p>
                <p className="mt-1 font-display text-3xl font-bold text-ink">{members.length}</p>
                <p className="text-xs text-ink-muted">{withData.length} reporting data</p>
              </div>
              <div className="rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
                <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">Busiest now</p>
                <p className="mt-1 font-display text-3xl font-bold text-ink">
                  {busiest ? busiest.now!.toLocaleString() : "—"}
                </p>
                <p className="truncate text-xs text-ink-muted">{busiest ? busiest.name : "no data yet"}</p>
              </div>
              <div className="rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
                <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">Combined</p>
                <p className="mt-1 font-display text-3xl font-bold text-ink">
                  {combined ? combined.toLocaleString() : "—"}
                </p>
                <p className="text-xs text-ink-muted">sum of areas · may overlap</p>
              </div>
            </div>

            {/* Overlaid time series */}
            <div className="mb-6 rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-lg font-semibold text-ink">
                  Subscribers present — by area
                  {bucketed && (
                    <span className="ml-2 rounded bg-paper-200 px-1.5 py-0.5 align-middle text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                      {bucket === "day" ? "daily" : "hourly"} average
                    </span>
                  )}
                  {nightFloor.enabled && floorByMember.size > 0 && (
                    <span className="ml-2 rounded bg-brand-50 px-1.5 py-0.5 align-middle text-[10px] font-mono uppercase tracking-wider text-brand-700">
                      night floor deducted
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportCsv}
                    disabled={!members.some((m) => m.series.length)}
                    className="rounded-lg border border-paper-300 px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-paper-100 disabled:opacity-40"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
              <div className="h-80">
                <AreaTimelineChart series={chartSeries} />
              </div>
            </div>

            {/* Leaderboard */}
            <div className="overflow-hidden rounded-2xl border border-paper-300/70 bg-surface">
              <div className="border-b border-paper-200 px-4 py-3">
                <span className="text-sm font-medium text-ink">Areas · ranked by present now</span>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-paper-100">
                    <tr>
                      {["Area", "Present", "Peak", "Trend (1h)"].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-ink-faint"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((m) => {
                      const elsewhere = !realtimeDataIsHere(m.bqProjectId);
                      return (
                      <tr key={m.id} className={`border-t border-paper-200 ${elsewhere ? "opacity-50" : ""}`}>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-2">
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: m.color }}
                            />
                            <span className="text-ink">{m.name}</span>
                            {elsewhere && (
                              <span className="rounded bg-warn-soft px-1.5 py-0.5 text-[10px] font-medium text-warn-ink">
                                {realtimeEnvLabel(m.bqProjectId)}
                              </span>
                            )}
                            {nightFloor.enabled && m.now != null && !m.baselineAdjusted && (
                              <span
                                className="rounded bg-paper-200 px-1.5 py-0.5 text-[10px] font-medium text-ink-muted"
                                title="Not enough night-time data in the selected nights — counts shown raw"
                              >
                                raw
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-2 tabular-nums text-ink">
                          {m.now != null ? m.now.toLocaleString() : <span className="text-ink-faint">—</span>}
                        </td>
                        <td className="px-4 py-2 tabular-nums text-ink-muted">
                          {m.peak != null ? m.peak.toLocaleString() : "—"}
                        </td>
                        <td className="px-4 py-2">
                          {m.delta == null ? (
                            <span className="text-ink-faint">—</span>
                          ) : (
                            <span
                              className={
                                m.delta > 0 ? "text-ok-ink" : m.delta < 0 ? "text-bad-ink" : "text-ink-muted"
                              }
                            >
                              {m.delta > 0 ? "▲" : m.delta < 0 ? "▼" : "—"} {Math.abs(m.pct!).toFixed(0)}%
                            </span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmShared !== null}
        title="Disable monitoring for all areas?"
        danger
        busy={monitorBusy}
        confirmLabel="Disable all"
        onConfirm={confirmDisableAll}
        onCancel={() => setConfirmShared(null)}
        body={
          <>
            <p>
              This stops live monitoring for {monitoredCount} area
              {monitoredCount === 1 ? "" : "s"} in{" "}
              <span className="font-medium text-ink">{name}</span>.
            </p>
            {confirmShared && <SharedProjectsWarning shared={confirmShared} />}
          </>
        }
      />
    </div>
  );
};

export default ProjectDashboard;
